import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trophy } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';
import { useVendorSuccessStoryForm } from '../../hooks/useVendorSuccessStoryForm';

const CreateVendorSuccessStoryModal = ({
 isOpen,
 onClose,
 onSave,
 story = null,
 isEdit = false,
 isLoading = false,
}) => {
 const [errors, setErrors] = useState({});

 const {
 formData,
 setFormData,
 selectedVendorId,
 setSelectedVendorId,
 vendorOptions,
 categoryOptions,
 isLoadingVendors,
 isLoadingCategories,
 } = useVendorSuccessStoryForm({ isOpen, isEdit, story });


 const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
 setFormData((prev) => ({
 ...prev,
 [name]: type === 'checkbox' ? checked : value,
 }));
 if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
 };

 const handleStatsChange = (key, value) => {
 setFormData((prev) => ({
 ...prev,
 stats: { ...prev.stats, [key]: value },
 }));
 };

 const validateForm = () => {
 const newErrors = {};
 if (!formData.vendor_name.trim()) newErrors.vendor_name = 'Vendor name is required';
 if (!formData.category.trim()) newErrors.category = 'Category is required';
 if (!formData.quote.trim()) newErrors.quote = 'Quote is required';
 if (!formData.author.trim()) newErrors.author = 'Author is required';
 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validateForm()) return;

 const stats = {};
 if (formData.stats.bookings !== '' && formData.stats.bookings != null)
 stats.bookings = parseInt(formData.stats.bookings) || 0;
 if (formData.stats.rating !== '' && formData.stats.rating != null)
 stats.rating = parseFloat(formData.stats.rating) || 0;
 if (formData.stats.revenue && String(formData.stats.revenue).trim())
 stats.revenue = String(formData.stats.revenue).trim();

 const submitData = {
 vendor_name: formData.vendor_name.trim(),
 vendor_id: formData.vendor_id || null,
 category: formData.category,
 image: formData.image.trim() || null,
 quote: formData.quote.trim(),
 author: formData.author.trim(),
 role: formData.role.trim() || null,
 stats: Object.keys(stats).length > 0 ? stats : null,
 featured: formData.featured,
 sort_order: parseInt(formData.sort_order) || 0,
 is_active: formData.is_active,
 };

 onSave(submitData);
 };

 const handleClose = () => {
 setErrors({});
 onClose();
 };

 if (!isOpen) return null;

 return createPortal(
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
 <div className="bg-surface rounded-lg shadow-sm max-w-2xl w-full max-h-[95vh] overflow-y-auto relative my-4">
 <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-surface z-10">
 <div className="flex items-center gap-3">
 <div className="h-8 w-8 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center">
 <Trophy className="h-4 w-4 text-white" />
 </div>
 <h3 className="text-lg font-medium text-gray-900">
 {isEdit ? 'Edit Success Story' : 'Create Success Story'}
 </h3>
 </div>
 <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
 <X className="h-5 w-5" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="p-4 space-y-4">
 {/* Vendor Selection - link story to vendor (create + edit) */}
 <div className="p-3 sm:p-4 bg-surface-muted border border-border rounded-lg">
 <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2 sm:mb-3">
 {isEdit ? 'Link to Vendor (for View Vendor Profile button)' : 'Select Vendor to Auto-Populate Details'}
 </h4>
 <SearchSelect
 label="Search Active Vendors"
 value={selectedVendorId}
 onChange={(value) => {
 setSelectedVendorId(value);
 setErrors((prev) => ({ ...prev, vendor_name: '' }));
 }}
 options={vendorOptions}
 placeholder="Search and select a vendor..."
 searchPlaceholder="Type to search vendors..."
 clearable
 searchable
 loading={isLoadingVendors}
 className="w-full"
 />
 {selectedVendorId && (
 <p className="mt-2 text-xs text-gray-600">
 ✓ Vendor selected. {isEdit ? 'Story will link to this vendor\'s profile.' : 'Name, image, category, and stats will be auto-populated.'}
 </p>
 )}
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <Input
 label="Vendor Name *"
 name="vendor_name"
 value={formData.vendor_name}
 onChange={handleChange}
 placeholder="e.g. Elite Photo Studios"
 error={errors.vendor_name}
 required
 />
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
 <SearchSelect
 value={formData.category}
 onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
 options={categoryOptions}
 placeholder="Select category..."
 searchPlaceholder="Search categories..."
 searchable
 clearable={false}
 loading={isLoadingCategories}
 className="w-full"
 />
 {errors.category && (
 <p className="mt-1 text-sm text-red-600">{errors.category}</p>
 )}
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
 <div className="space-y-2">
 <Input
 name="image"
 value={formData.image}
 onChange={handleChange}
 placeholder="https://example.com/image.jpg"
 type="url"
 />
 {formData.image && (
 <div className="relative w-full max-w-xs">
 <img
 src={formData.image}
 alt="Preview"
 className="w-full h-32 object-cover rounded-lg border border-gray-200"
 onError={(e) => (e.target.style.display = 'none')}
 />
 </div>
 )}
 </div>
 </div>

 <TextArea
 label="Quote *"
 name="quote"
 value={formData.quote}
 onChange={handleChange}
 placeholder="Testimonial quote from the vendor"
 rows={4}
 error={errors.quote}
 required
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <Input
 label="Author *"
 name="author"
 value={formData.author}
 onChange={handleChange}
 placeholder="Current admin"
 error={errors.author}
 required
 />
 <Input
 label="Role"
 name="role"
 value={formData.role}
 onChange={handleChange}
 placeholder="Administrator"
 />
 </div>

 <div>
 <h4 className="text-sm font-medium text-gray-900 mb-2">Stats (optional, auto-filled from vendor)</h4>
 <div className="grid grid-cols-3 gap-3">
 <Input
 label="Bookings"
 type="number"
 min="0"
 value={formData.stats.bookings}
 onChange={(e) => handleStatsChange('bookings', e.target.value)}
 placeholder="150"
 />
 <Input
 label="Rating"
 type="number"
 step="0.1"
 min="0"
 max="5"
 value={formData.stats.rating}
 onChange={(e) => handleStatsChange('rating', e.target.value)}
 placeholder="4.9"
 />
 <Input
 label="Revenue"
 value={formData.stats.revenue}
 onChange={(e) => handleStatsChange('revenue', e.target.value)}
 placeholder="$45K"
 />
 </div>
 </div>

 <div className="flex items-center gap-6">
 <label className="flex items-center gap-2">
 <input
 type="checkbox"
 name="featured"
 checked={formData.featured}
 onChange={handleChange}
 className="h-4 w-4 text-ink rounded border-gray-300"
 />
 <span className="text-sm text-gray-700">Featured</span>
 </label>
 <label className="flex items-center gap-2">
 <input
 type="checkbox"
 name="is_active"
 checked={formData.is_active}
 onChange={handleChange}
 className="h-4 w-4 text-ink rounded border-gray-300"
 />
 <span className="text-sm text-gray-700">Active</span>
 </label>
 <div className="flex-1">
 <Input
 label="Sort Order"
 name="sort_order"
 type="number"
 min="0"
 value={formData.sort_order}
 onChange={handleChange}
 placeholder="0"
 />
 </div>
 </div>

 <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
 <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>
 Cancel
 </Button>
 <Button type="submit" disabled={isLoading}>
 {isLoading ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
 {isEdit ? 'Updating...' : 'Creating...'}
 </>
 ) : (
 isEdit ? 'Update Story' : 'Create Story'
 )}
 </Button>
 </div>
 </form>
 </div>
 </div>,
 document.body
 );
};

export default CreateVendorSuccessStoryModal;
